"use client";

import { inView } from "motion";
import { animate } from "motion/mini";
import { useEffect, useLayoutEffect, type RefObject } from "react";

/**
 * The block reveal engine. A section hands it its own element; the engine finds
 * the containers marked `data-reveal` inside that section and plays their direct
 * children the first time they are reached:
 *
 * - `data-reveal="stack"` — copy settles in reading order.
 * - `data-reveal="stagger"` — a grid fills in reading order.
 * - `data-reveal="count"` — one element that counts its own number up. It is
 *   self-triggered, so it can sit anywhere, including inside a staggering grid.
 * - `data-reveal-delay="200"` — on a container, the group's own start offset from
 *   its trigger; on a child, that child's offset inside the group, replacing the
 *   slot it would otherwise take. An overridden child keeps its slot, so nothing
 *   downstream of it moves.
 * - `data-reveal-step="60"` — the gap between consecutive children. Every child
 *   takes its own slot: item i starts at i × step, whatever the item count.
 * - `data-reveal-enter="wipe"` — the material, on a container (for all of its
 *   children) or on one child. Materials name a role rather than a preference:
 *   `rise` for copy, `wipe` for a display heading, `focus` for a media panel.
 * - `data-reveal-skip` — a child that takes part in the layout but not in the
 *   sequence, such as a decorative texture. A skipped child takes no slot, so the
 *   children after it keep the times they already had.
 *
 * Timing defaults live here rather than in the blocks: the rhythm is data, the
 * voice is not. Duration, easing, and distance stay in this file because
 * DESIGN.md pins them for the whole world.
 *
 * Three properties matter more than the choreography:
 *
 * 1. Nothing is hidden in the markup. Only a container that is still entirely
 *    below the viewport is armed, and it is armed in a layout effect, so a
 *    failed script, a crawler, or `prefers-reduced-motion` leaves the page
 *    exactly as the server sent it. Anything already on screen at load is left
 *    alone: visible content is never hidden so that it can be shown again.
 * 2. Every element is released when its animation ends. The reveal writes inline
 *    style, which would otherwise outrank the hover styles the blocks carry.
 * 3. A throw anywhere leaves the page readable instead of blank.
 */

const EASE = [0.16, 1, 0.3, 1] as const;
/**
 * Long enough to be watched rather than merely gone. An exponential curve does
 * most of its work in the first third, so a 400ms settle was over in about
 * 150ms of visible change and read as a jump cut on anything the visitor was not
 * already looking at. Slower feedback would be latency; this is an entrance.
 */
const SETTLE_SECONDS = 0.7;
const RISE_PX = 12;
const LINE_STEP_MS = 100;
const ITEM_LEAD_MS = 300;
const ITEM_STEP_MS = 60;
const COUNT_SECONDS = 1.1;
const ENTRY_MARGIN = "0px 0px -12% 0px";

type EnterName = "rise" | "wipe" | "focus";

/**
 * `rise` is the template's default and the only material used broadly. The other
 * two are kept for a single role each, so no page reads as an effects reel:
 * a display heading is wiped onto the page, and a media panel resolves like a
 * lens — the same idea as the hero surface, at a fraction of the cost.
 */
const ENTERS: Record<EnterName, { from: Record<string, string>; to: Record<string, string> }> = {
  rise: {
    from: { opacity: "0", transform: `translateY(${RISE_PX}px)` },
    to: { opacity: "1", transform: "translateY(0px)" },
  },
  wipe: {
    from: { clipPath: "inset(0% 0% 100% 0%)", opacity: "0" },
    to: { clipPath: "inset(0% 0% 0% 0%)", opacity: "1" },
  },
  focus: {
    from: { filter: "blur(3px)", opacity: "0", transform: "scale(1.02)" },
    to: { filter: "blur(0px)", opacity: "1", transform: "scale(1)" },
  },
};

/** `useLayoutEffect` warns when a server render can never run it. */
const useIsomorphicLayoutEffect = typeof window === "undefined" ? useEffect : useLayoutEffect;

/**
 * React reuses a component instance when the next page renders the same type in
 * the same position — two service pages do exactly that — so an effect keyed on
 * the ref alone would keep the previous page's outcome and never animate the new
 * children. The pathname is the cheapest signal that this section now belongs to
 * another page; it is read during render, so the re-run lands in the same commit
 * as the content it has to animate.
 */
const usePageKey = () => (typeof window === "undefined" ? "" : window.location.pathname);

const unreached = (element: HTMLElement) =>
  element.getBoundingClientRect().top >= window.innerHeight;

const readEnter = (element: HTMLElement, fallback: EnterName): EnterName => {
  const value = element.dataset.revealEnter;
  return value && value in ENTERS ? (value as EnterName) : fallback;
};

const styled = (element: HTMLElement) => element.style as unknown as Record<string, string>;

const hold = (element: HTMLElement, enter: EnterName) => {
  for (const [property, value] of Object.entries(ENTERS[enter].from)) {
    styled(element)[property] = value;
  }
};

const release = (element: HTMLElement, enter: EnterName) => {
  for (const property of Object.keys(ENTERS[enter].from)) {
    styled(element)[property] = "";
  }
};

const keyframesFor = (enter: EnterName) =>
  Object.fromEntries(
    Object.entries(ENTERS[enter].from).map(([property, from]) => [
      property,
      [from, ENTERS[enter].to[property]],
    ]),
  );

/** Milliseconds from a data attribute. Empty or nonsense values fall back. */
const readMs = (value: string | undefined) => {
  if (value === undefined || value.trim() === "") return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : undefined;
};

type Reveal = { delay: number; element: HTMLElement; enter: EnterName };

const planReveal = (container: HTMLElement): Reveal[] | null => {
  const grid = container.dataset.reveal === "stagger";
  const elements = Array.from(container.children).filter(
    (child): child is HTMLElement =>
      child instanceof HTMLElement && !child.hasAttribute("data-reveal-skip"),
  );
  if (!elements.length) return null;

  const leaf = readEnter(container, "rise");
  const lead = readMs(container.dataset.revealDelay) ?? (grid ? ITEM_LEAD_MS : 0);
  const step = readMs(container.dataset.revealStep) ?? (grid ? ITEM_STEP_MS : LINE_STEP_MS);

  // Nothing ranks, caps, or compresses this timeline. A hundred items at 100ms
  // cascade for ten seconds because that is what the step says; a block that
  // wants a shorter wave authors a smaller step. An earlier version bounded the
  // window at twelve slots, which silently stopped the stagger and dropped every
  // later item on the same timestamp — a policy the content never agreed to.
  return elements.map((element, index) => ({
    delay: lead + (readMs(element.dataset.revealDelay) ?? index * step),
    element,
    enter: readEnter(element, leaf),
  }));
};

const COUNT_PATTERN = /^([^\d]*)(\d[\d.,]*)(.*)$/su;

/** Splits "4.2k" into a prefix, a number, its decimal places, and a suffix. */
const parseCount = (text: string) => {
  const match = COUNT_PATTERN.exec(text.trim());
  if (!match) return null;
  const [, prefix = "", numeric = "", suffix = ""] = match;
  const target = Number(numeric.replace(/,/gu, ""));
  if (!Number.isFinite(target)) return null;
  const decimals = /\.(\d+)$/u.exec(numeric)?.[1].length ?? 0;
  const number = new Intl.NumberFormat("en-US", {
    maximumFractionDigits: decimals,
    minimumFractionDigits: decimals,
    useGrouping: numeric.includes(","),
  });

  return {
    format: (value: number) => `${prefix}${number.format(value)}${suffix}`,
    target,
    text,
  };
};

/** Cubic ease-out. A numeric roll is not a spatial move, so it needs no more. */
const easeOut = (progress: number) => 1 - (1 - progress) ** 3;

export const useEffectfulHeroReveal = (ref: RefObject<HTMLElement | null>) => {
  const pageKey = usePageKey();

  useIsomorphicLayoutEffect(() => {
    const root = ref.current;
    if (!root || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const containers = Array.from(
      root.querySelectorAll<HTMLElement>('[data-reveal="stack"], [data-reveal="stagger"]'),
    );
    const counters = Array.from(root.querySelectorAll<HTMLElement>('[data-reveal="count"]'));
    if (!containers.length && !counters.length) return;

    const stopObservers: Array<() => void> = [];
    const stopAnimations: Array<() => void> = [];
    const pendingFrames: number[] = [];
    const heldElements: Array<{ element: HTMLElement; enter: EnterName }> = [];

    const play = (reveal: Reveal[]) => {
      for (const { delay, element, enter } of reveal) {
        try {
          const playback = animate(element, keyframesFor(enter), {
            delay: delay / 1000,
            duration: SETTLE_SECONDS,
            ease: EASE,
          });
          stopAnimations.push(() => playback.stop());
          playback.then(() => release(element, enter));
        } catch {
          release(element, enter);
        }
      }
    };

    try {
      for (const container of containers) {
        const reveal = planReveal(container);
        if (!reveal) continue;

        for (const { element, enter } of reveal) {
          hold(element, enter);
          heldElements.push({ element, enter });
        }

        // A group standing in the viewport when the page opens plays now: a
        // visitor landing on a hero should watch it arrive, not find it already
        // settled. Nothing is observed in that case. A group still below the
        // fold gets one observer, which is disconnected the moment it fires —
        // so nothing stays watched after the animation it was watching for.
        if (!unreached(container)) {
          play(reveal);
          continue;
        }

        const stop = inView(
          container,
          () => {
            stop();
            play(reveal);
          },
          { amount: "some", margin: ENTRY_MARGIN },
        );
        stopObservers.push(stop);
      }

      for (const counter of counters) {
        // The authored value comes from its own attribute, never from the live
        // text: a page change can land mid-count, and re-reading a half-counted
        // number would adopt it as the target.
        const authored = counter.dataset.revealCount ?? counter.textContent ?? "";
        const parsed = parseCount(authored);
        if (!parsed) continue;

        // The final frame always restores the authored string, so the count can
        // never be the thing that changes what the page says.
        counter.textContent = parsed.format(0);

        const step = (started: number) => {
          const progress = Math.min((performance.now() - started) / (COUNT_SECONDS * 1000), 1);
          counter.textContent =
            progress < 1 ? parsed.format(parsed.target * easeOut(progress)) : parsed.text;
          if (progress < 1) pendingFrames.push(requestAnimationFrame(() => step(started)));
        };

        if (!unreached(counter)) {
          pendingFrames.push(requestAnimationFrame(() => step(performance.now())));
          continue;
        }

        const stop = inView(
          counter,
          () => {
            stop();
            pendingFrames.push(requestAnimationFrame(() => step(performance.now())));
          },
          { amount: "some", margin: ENTRY_MARGIN },
        );
        stopObservers.push(stop);
      }
    } catch {
      heldElements.forEach(({ element, enter }) => release(element, enter));
      return;
    }

    return () => {
      stopObservers.forEach((stop) => stop());
      stopAnimations.forEach((stop) => stop());
      pendingFrames.forEach((frame) => cancelAnimationFrame(frame));
      // A page change can reuse these nodes with content that has no marker at
      // all, so the held state is given back rather than left on the element.
      heldElements.forEach(({ element, enter }) => release(element, enter));
    };
  }, [ref, pageKey]);
};
